import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { EventPollQuestion } from '../../../interfaces/interfaces';
import { fetchAndSetEventAnsweredPoll, fetchAndSetMeetingAnsweredPoll, setEventPoll, setMeetingPoll } from '../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../reduxToolkit/typedRedux';
import { pollNotifyVisitor } from '../../../services/helper';
import { useMeetingPermission } from '../../meeting/hooks/useMeetingPermission';

const PollNotification = () => {
  const event = useSelector((state) => state.common.event);
  const user = useSelector((state) => state.common.user);
  const meeting = useSelector(({ video }) => video.meeting);

  const eventPoll = useSelector((state) => state.common.eventPoll);
  const meetingPoll = useSelector((state) => state.common.meetingPoll);
  const eventAnsweredPolls = useSelector((state) => state.common.eventAnsweredPolls);
  const meetingAnsweredPolls = useSelector((state) => state.common.meetingAnsweredPolls);
  const pusherChannels = useSelector((state) => state.common.pusherChannels);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const permission = useMeetingPermission();

  const eventPollRef = useRef<EventPollQuestion[] | undefined>();
  const meetingPollRef = useRef<EventPollQuestion[] | undefined>();
  const previousToastRef = useRef<string | undefined>();

  function showToast() {
    if (previousToastRef.current) {
      toast.dismiss(previousToastRef.current);
    }

    previousToastRef.current = toast(t('poll:user-notify-new-poll'));
  }

  useEffect(() => {
    if (event && !meeting) dispatch(fetchAndSetEventAnsweredPoll(event._id));
    if (meeting) dispatch(fetchAndSetMeetingAnsweredPoll(meeting._id));
  }, [event, meeting]);

  useEffect(() => {
    eventPollRef.current = eventPoll;
  }, [eventPoll]);

  useEffect(() => {
    if (eventAnsweredPolls) dispatch(setEventPoll(eventPoll?.slice().map((p) => ({ ...p, isAnswered: eventAnsweredPolls.includes(p._id) }))));
  }, [eventAnsweredPolls]);

  useEffect(() => {
    meetingPollRef.current = meetingPoll;
  }, [meetingPoll]);

  useEffect(() => {
    if (meetingAnsweredPolls) dispatch(setMeetingPoll(meetingPoll?.slice().map((p) => ({ ...p, isAnswered: meetingAnsweredPolls.includes(p._id) }))));
  }, [meetingAnsweredPolls]);

  useEffect(() => {
    if (!pusherChannels?.eventChannel) {
      return;
    }
    pusherChannels.eventChannel.bind(`pollUpdated`, ({ data }: { data: EventPollQuestion }) => {
      const question = eventPollRef.current?.find((e: EventPollQuestion) => {
        return e._id === data?._id;
      });
      if (!question) {
        // if we don't have the question in the redux we just append it to the state
        dispatch(setEventPoll([...(eventPollRef.current ? eventPollRef.current : []), data]));
      } else {
        // otherwise we update the question in the state
        if (eventAnsweredPolls && eventPollRef.current) {
          dispatch(
            setEventPoll([
              ...eventPollRef.current.map((item: EventPollQuestion) => {
                if (item._id === data._id) return { ...data, isAnswered: eventAnsweredPolls.includes(data._id) };
                else return item;
              }),
            ])
          );
          if (!meeting && user?.role !== 'ORGANIZATION_EXTERNAL_SPEAKER' && event && user?.organization?._id !== event?.organization?._id) {
            if (pollNotifyVisitor([question], data, eventAnsweredPolls)) {
              showToast();
            }
          }
        }
      }
    });
    return () => {
      if (pusherChannels.eventChannel) pusherChannels.eventChannel.unbind(`pollUpdated`);
    };
  }, [pusherChannels]);

  useEffect(() => {
    if (!pusherChannels?.meetingChannel) {
      return;
    }

    pusherChannels.meetingChannel.bind(`pollMeetingUpdated`, ({ data }: { data: EventPollQuestion }) => {
      const index = meetingPollRef.current?.findIndex((e: EventPollQuestion) => {
        return e._id === data?._id;
      });
      if (index === -1) {
        // if we don't have the question in the redux we just append it to the state
        dispatch(setMeetingPoll([...(meetingPollRef.current ? meetingPollRef.current : []), data]));
      } else {
        // otherwise we update the question in the state
        if (meetingAnsweredPolls && meetingPollRef.current) {
          dispatch(
            setMeetingPoll([
              ...meetingPollRef.current.map((item: EventPollQuestion) => {
                if (item._id === data._id) return { ...data, isAnswered: meetingAnsweredPolls.includes(data._id) };
                else return item;
              }),
            ])
          );
          if (meeting && !permission.isAdmin()) {
            if (pollNotifyVisitor(meetingPollRef.current, data, meetingAnsweredPolls)) {
              showToast();
            }
          }
        }
      }
    });
    return () => {
      if (pusherChannels.meetingChannel) pusherChannels.meetingChannel.unbind(`pollMeetingUpdated`);
    };
  }, [pusherChannels]);

  return <></>;
};
export default PollNotification;
